import React, { useState } from "react";
import {
  Table,
  Tag,
  Spin,
  Alert,
  Drawer,
  Button,
  Dropdown,
  Menu,
  Typography,
  Form,
  Modal,
  Input,
  Select,
  Upload,
  message,
  Popconfirm,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import useGetRequest from "../../hooks/useGetRequest";
import { IFile, IFileVersion, IUserInfo } from "../../types/file.type";
import { DownOutlined, UploadOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { canAddFiles, canEdit } from "../../utils/roleUtils";

export default function Files() {
  const { authState } = useAuth();
  const { data, loading, error, refresh } = useGetRequest("/files");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IFile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const { data: usersList, loading: usersLoading } = useGetRequest("/user");

  const showVersions = (file: IFile) => {
    setSelectedFile(file);
    setDrawerOpen(true);
  };

  const handleDownload = (version: IFileVersion) => {
    window.open(api.defaults.baseURL + "/" + version.filePath, "_blank");
  };

  const handleOpenModal = (file?: IFile) => {
    if (file) {
      setSelectedFile(file);
      form.setFieldsValue({
        name: file.name,
        accessUsers: file.accessUsers.map((user) => 
          typeof user === 'string' ? user : user._id
        ),
      });
    } else {
      setSelectedFile(null);
      form.resetFields();
    }

    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/files/${id}`);
      message.success("Файл успешно удален.");
      refresh();
    } catch (error: any) {
      console.error("Error deleting file", error);
      message.error(error.response.data.error);
    }
  };

  const handleSubmitForm = async (values: any) => {
    const formData = new FormData();
    formData.append("name", values.name);

    if (values.accessUsers) {
      values.accessUsers.forEach((userId: string) =>
        formData.append("accessUsers", userId)
      );
    }

    const file = values.file?.[0]?.originFileObj;
    if (file) {
      formData.append("file", file);
    }

    try {
      setConfirmLoading(true);
      if (selectedFile) {
        await api.put(`/files/${selectedFile._id}`, formData);
        message.success("Файл успешно обновлен.");
      } else {
        await api.post("/files", formData);
        message.success("Файл успешно добавлен.");
      }

      setIsModalOpen(false);
      form.resetFields();
      refresh();
    } catch (error: any) {
      console.error("Ошибка при отправке:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const versionColumns: ColumnsType<IFileVersion> = [
    {
      title: "Версия",
      dataIndex: "version",
      key: "version",
    },
    {
      title: "Имя файла",
      dataIndex: "fileName",
      key: "fileName",
    },
    {
      title: "Размер (МБ)",
      dataIndex: "size",
      key: "size",
      render: (size: number) => (size / (1024 * 1024)).toFixed(2),
    },
    {
      title: "Загрузил",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (u: IUserInfo | string) => (typeof u === "string" ? u : u.name),
    },
    {
      title: "Дата загрузки",
      dataIndex: "uploadedAt",
      key: "uploadedAt",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, fileVersion: IFileVersion) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleDownload(fileVersion)}>
              Скачать
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const columns: ColumnsType<IFile> = [
    {
      title: "Название файла",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Создатель",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (u: IUserInfo | string) => (typeof u === "string" ? u : u.name),
    },
    {
      title: "Доступ имеют",
      dataIndex: "accessUsers",
      key: "accessUsers",
      render: (users: (IUserInfo | string)[]) =>
        users.map((u, idx) => (
          <Tag key={idx} color="blue">
            {typeof u === "string" ? u : u.name}
          </Tag>
        )),
    },
    {
      title: "Текущая версия",
      dataIndex: "currentVersion",
      key: "currentVersion",
    },
    {
      title: "Версии",
      key: "versions",
      render: (_, file) => (
        <Button type="link" onClick={() => showVersions(file)}>
          Смотреть версии
        </Button>
      ),
    },
    {
      title: "Дата создания",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "Обновлено",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, file: IFile) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() =>
                handleDownload(file.versions[file.versions.length - 1])
              }
            >
              Скачать
            </Menu.Item>
            {canEdit(authState.user?.role) && (
              <>
                <Menu.Item onClick={() => handleOpenModal(file)}>
                  Изменить
                </Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этот файл?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDelete(file._id)}
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (loading) return <Spin />;
  if (error) return <Alert message="Ошибка загрузки" type="error" />;

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title>Файлы</Typography.Title>
      {canAddFiles(authState.user?.role) && (
        <Button
          type="primary"
          style={{ marginBottom: 16 }}
          onClick={() => handleOpenModal()}
        >
          Добавить
        </Button>
      )}
      <Table
        rowKey={(record) => record.name + record.createdAt}
        columns={columns}
        dataSource={data || []}
        bordered
      />

      <Drawer
        title={`Версии файла: ${selectedFile?.name}`}
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={700}
      >
        <Table
          rowKey="version"
          columns={versionColumns}
          dataSource={selectedFile?.versions || []}
          pagination={false}
        />
      </Drawer>

      <Modal
        title={selectedFile ? "Изменить" : "Добавить"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitForm}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите Название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="accessUsers" label="Доступ">
            <Select
              mode="multiple"
              loading={usersLoading}
              placeholder="Выберите пользователей"
              optionFilterProp="children"
            >
              {usersList?.users.map((user: { _id: string; name: string }) => (
                <Select.Option key={user._id} value={user._id}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="file"
            label="Файл"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
          >
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
